import { Col, FormInstance, Row } from 'antd';
import React, { FC, useCallback, useRef } from 'react';
import {
  JiraConfig,
  JiraEpic,
  JiraIssueType,
  JiraProject,
  MappingMechanism,
  ProjectMappingMechanism,
} from 'api/integrations/jira';
import { ExternalResourceInputs } from 'api/tasks';
import { Form } from 'components/atoms/form';
import { RichTextEditor } from 'components/atoms/rich-text-editor';
import { Select } from 'components/atoms/select';
import { useTranslation } from 'hooks/use-translation';
import variableStyles from 'vendor/ckeditor/plugins/variables/variable.module.css';

type JiraTemplatesForm = {
  jiraConfig: JiraConfig;
  form: FormInstance<ExternalResourceInputs>;
  onChange: () => void;
  onProjectChange: (projectId: string) => void;
  onIssueTypeChange: (issueTypeId: string) => void;
  onEpicChange: (epicId: string) => void;
  issueTypes?: JiraIssueType[];
  loadingIssueTypes?: boolean;
  epicsList?: JiraEpic[];
  loadingEpicsList?: boolean;
  jiraProjects?: JiraProject[];
  loadingJiraProjects?: boolean;
  disabled?: boolean;
};

type FormValues<T> = Record<'epicId' | 'issuetype' | 'projectId', T>;

const JiraSummary = ({
  data,
  onChange,
  disabled,
}: {
  data: string;
  onChange: (_data) => void;
  disabled?: boolean;
}) => {
  const updateRef = useRef<RichTextEditor>(null);

  const setData = useCallback(() => {
    updateRef.current?.setData();
  }, []);

  return (
    <RichTextEditor
      className={`${variableStyles.form} ${variableStyles['jira-templates-form']}`}
      data={data}
      noToolbar
      noFormatting
      disallowMentions
      disallowImages
      allowSimpleVariables
      disallowNewLine
      onChange={onChange}
      readonly={disabled}
      ref={updateRef}
      onInit={setData}
    />
  );
};

const JiraDescription = ({
  data,
  onChange,
  disabled,
}: {
  data: string;
  onChange: (_data) => void;
  disabled?: boolean;
}) => {
  const updateRef = useRef<RichTextEditor>(null);

  const setData = useCallback(() => {
    updateRef.current?.setData();
  }, []);

  return (
    <RichTextEditor
      className={`${variableStyles.form}`}
      data={data}
      noToolbar
      noFormatting
      disallowMentions
      disallowImages
      allowSimpleVariables
      disallowNewLine
      onChange={onChange}
      readonly={disabled}
      ref={updateRef}
      onInit={setData}
    />
  );
};

export const JiraTemplatesForm: FC<JiraTemplatesForm> = ({
  jiraConfig,
  form,
  onChange,
  onProjectChange,
  onIssueTypeChange,
  onEpicChange,
  issueTypes,
  loadingIssueTypes,
  epicsList,
  loadingEpicsList,
  jiraProjects,
  loadingJiraProjects,
  disabled,
}) => {
  const requiredMessage = useTranslation('input.required');

  const {
    settingsMeta: { epicMappingMechanism, projectMappingMechanism },
  } = jiraConfig;

  const promptMappingPlaceholder = useTranslation(
    'templates.jira.prompt_project_creation.text',
  );
  const automaticMappingPlaceholder = useTranslation(
    'templates.jira.automatic.text',
  );
  const fixedProjectHelpText = useTranslation('templates.jira.project.help');

  const issueTypeLabel = useTranslation('issue_type');
  const epicIdLabel = useTranslation('epic');
  const projectLabel = useTranslation('project');

  const placeholder =
    epicMappingMechanism === MappingMechanism.PromptDuringProjectcreation
      ? promptMappingPlaceholder
      : epicMappingMechanism === MappingMechanism.Automatic
      ? automaticMappingPlaceholder
      : undefined;

  const isFixedProjectMapping =
    projectMappingMechanism === ProjectMappingMechanism.Fixed;
  const disableEpicSelection =
    isFixedProjectMapping &&
    epicMappingMechanism !== MappingMechanism.PromptDuringIssuecreation;

  return (
    <Form
      layout="vertical"
      form={form}
      onValuesChange={(value: FormValues<{ label: string; value: string }>) => {
        if (value?.issuetype) {
          return onIssueTypeChange(value.issuetype?.value);
        }
        if (value?.epicId) {
          return onEpicChange(value.epicId?.value);
        }
        if (value?.projectId) {
          return onProjectChange(value.projectId?.value);
        }

        return onChange();
      }}
      scrollToFirstError
    >
      {/* Issue type */}
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Form.Item
            name="projectId"
            label={projectLabel}
            rules={[{ required: true, message: requiredMessage }]}
            help={isFixedProjectMapping ? fixedProjectHelpText : undefined}
          >
            <Select
              options={(jiraProjects ?? []).map(({ id, name }) => ({
                value: id,
                label: name,
              }))}
              isLoading={loadingJiraProjects}
              isDisabled={isFixedProjectMapping}
            />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item
            name="issuetype"
            label={issueTypeLabel}
            rules={[{ required: true, message: requiredMessage }]}
          >
            <Select
              options={(issueTypes || []).map((issueTypeOption) => ({
                value: issueTypeOption.id,
                label: issueTypeOption.name,
              }))}
              isDisabled={disabled}
              isLoading={loadingIssueTypes}
            />
          </Form.Item>
        </Col>

        {/* Epic */}
        <Col span={12}>
          <Form.Item
            label={epicIdLabel}
            name="epicId"
            help={disableEpicSelection ? placeholder : undefined}
          >
            <Select
              placeholder={disableEpicSelection ? '—' : undefined}
              isLoading={loadingEpicsList}
              options={(epicsList || [])?.map((epic) => ({
                value: epic.id,
                label: epic.name,
              }))}
              isDisabled={disabled || disableEpicSelection}
            />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item
            label="Summary"
            rules={[
              { required: true, message: requiredMessage, whitespace: true },
            ]}
            shouldUpdate={(prev, current) => prev.summary !== current.summary}
          >
            {({ getFieldValue, setFieldsValue }) => {
              const _summary = getFieldValue('summary');
              const data = !_summary
                ? ''
                : typeof _summary === 'object'
                ? _summary.value ?? ''
                : typeof _summary === 'string'
                ? _summary
                : '';

              return (
                <JiraSummary
                  data={data}
                  disabled={disabled}
                  onChange={(content) =>
                    setFieldsValue({
                      summary: {
                        ...getFieldValue('summary'),
                        value: content,
                      },
                    })
                  }
                />
              );
            }}
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item
            label="Description"
            shouldUpdate={(prev, current) =>
              prev.description !== current.description
            }
          >
            {({ getFieldValue, setFieldsValue }) => {
              const _description = getFieldValue('description');
              const data = !_description
                ? ''
                : typeof _description === 'object'
                ? _description.value ?? ''
                : typeof _description === 'string'
                ? _description
                : '';

              return (
                <JiraDescription
                  data={data}
                  disabled={disabled}
                  onChange={(content) =>
                    setFieldsValue({
                      description: {
                        ...getFieldValue('description'),
                        value: content,
                      },
                    })
                  }
                />
              );
            }}
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};
